import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import moment, { Moment } from 'moment'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
} from '@mui/material'
import DatePicker from '../../../../components/muiComponents/datePicker/DatePicker'
import { courseTopicApiSlice } from '../../../../features/course/courseTopicSlice'
import { ActiveCourseTopicState } from '../../../../features/types'

interface Props {
    open: boolean
    handleClose: () => void
    topic: ActiveCourseTopicState | null
}

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const WEEKS = [1, 2, 3, 4]

const EditCourseTopicScheduleDialog: React.FC<Props> = ({
    open,
    handleClose,
    topic,
}) => {
    const { formatMessage } = useIntl()
    const [updateCourseTopic, { isLoading }] =
        courseTopicApiSlice.useUpdateCourseTopicMutation()

    const [monthNumber, setMonthNumber] = useState<number | ''>('')
    const [weekNumber, setWeekNumber] = useState<number | ''>('')
    const [startDate, setStartDate] = useState<Moment | null>(null)
    const [endDate, setEndDate] = useState<Moment | null>(null)

    useEffect(() => {
        if (!open) return
        setMonthNumber(topic?.monthNumber ?? '')
        setWeekNumber(topic?.weekNumber ?? '')
        setStartDate(topic?.startDate ? moment(topic.startDate) : null)
        setEndDate(topic?.endDate ? moment(topic.endDate) : null)
    }, [open, topic])

    const handleSubmit = async () => {
        if ((monthNumber === '') !== (weekNumber === '')) {
            toast.error(formatMessage({ id: 'monthWeekBothRequired' }))
            return
        }
        if (startDate && endDate && startDate.isAfter(endDate)) {
            toast.error(formatMessage({ id: 'startDateAfterEndDate' }))
            return
        }
        if (!topic) return

        try {
            await updateCourseTopic({
                id: topic.courseTopicId,
                body: {
                    monthNumber: monthNumber === '' ? null : monthNumber,
                    weekNumber: weekNumber === '' ? null : weekNumber,
                    startDate: startDate
                        ? startDate.format('YYYY-MM-DD')
                        : null,
                    endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
                },
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>{formatMessage({ id: 'editSchedule' })}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        select
                        label={formatMessage({ id: 'monthNumber' })}
                        value={monthNumber}
                        onChange={(e) =>
                            setMonthNumber(
                                e.target.value === ''
                                    ? ''
                                    : Number(e.target.value),
                            )
                        }
                    >
                        <MenuItem value="">-</MenuItem>
                        {MONTHS.map((m) => (
                            <MenuItem key={m} value={m}>
                                {m}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        select
                        label={formatMessage({ id: 'weekNumber' })}
                        value={weekNumber}
                        onChange={(e) =>
                            setWeekNumber(
                                e.target.value === ''
                                    ? ''
                                    : Number(e.target.value),
                            )
                        }
                    >
                        <MenuItem value="">-</MenuItem>
                        {WEEKS.map((w) => (
                            <MenuItem key={w} value={w}>
                                {w}
                            </MenuItem>
                        ))}
                    </TextField>

                    <DatePicker
                        label={formatMessage({ id: 'accessStartDate' })}
                        value={startDate}
                        handleChange={(value: Moment | null) =>
                            setStartDate(value)
                        }
                    />

                    <DatePicker
                        label={formatMessage({ id: 'accessEndDate' })}
                        value={endDate}
                        handleChange={(value: Moment | null) =>
                            setEndDate(value)
                        }
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    {formatMessage({ id: 'cancel' })}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {formatMessage({ id: 'save' })}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default EditCourseTopicScheduleDialog
